import { getFirstName, getInitials } from "@vincer/utils";
import { LogOut, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/lib/supabase";
import { useCurrentView } from "@/hooks/use-current-view";
import { useUser } from "@/hooks/useUser";

import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const StarRating = ({ rating }: { rating: number }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  return (
    <div className="flex items-center gap-1">
      <span className="mr-1 text-lg font-semibold text-primary">{rating}</span>
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={i} className="size-3 fill-yellow-400 text-yellow-400" />
        ))}
        {hasHalfStar && (
          <Star className="size-3 fill-yellow-400/50 text-yellow-400" />
        )}
        {[...Array(5 - Math.ceil(rating))].map((_, i) => (
          <Star key={`empty-${i}`} className="size-3 text-gray-300" />
        ))}
      </div>
    </div>
  );
};

export function Header() {
  const navigate = useNavigate();
  const { data: user } = useUser();
  const welcomeMessage = `Welcome back, ${getFirstName(user?.name)}`;
  const currentView = useCurrentView();

  const handleSignOut = async () => {
    await supabase.auth.signOut();

    navigate("/auth/login");
  };

  return (
    <header className="border-b border-primary/10 bg-white px-6 py-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {currentView.label}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {currentView.description}
          </p>
        </div>

        <div className="flex items-center gap-6">
          {/* Rating Display */}
          <div className="flex flex-col items-center">
            <p className="mb-1 text-xs text-muted-foreground">Rating</p>
            <StarRating rating={4.8} />
          </div>

          {/* Profile Section */}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-foreground">
                {welcomeMessage}
              </p>
              {user?.jobTitle && user?.company && (
                <p className="text-xs text-muted-foreground">
                  {user?.jobTitle}, {user?.company}
                </p>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button>
                  <Avatar>
                    <AvatarFallback className="bg-gradient-primary cursor-pointer text-sm text-white transition-all duration-200 hover:bg-primary">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="z-[1000] w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={"bottom"}
                align={"end"}
              >
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleSignOut();
                  }}
                >
                  <LogOut />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
